import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { BookOpen, Library, Flame, Award, Settings, Crown, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import Footer from '@/components/Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = 'https://yourpersonalshopper-storage.sfo3.digitaloceanspaces.com/omniread/omniread_logo.webp';

const CATEGORIES = [
  'Relationships', 'Work/Purpose', 'Identity', 'Healing',
  'Transformation/Shadow', 'Self-Worth', 'Grief/Letting Go',
  'Creativity', 'Leadership', 'Masculine/Feminine Dynamics', 'Courage'
];

export default function Dashboard({ user }) {
  const navigate = useNavigate();
  const [topic, setTopic] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [streak, setStreak] = useState({ current_streak: 0, longest_streak: 0 });
  const [badges, setBadges] = useState([]);
  const [profile, setProfile] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [streakResp, badgesResp, profileResp, subResp] = await Promise.all([
        axios.get(`${API}/streak`),
        axios.get(`${API}/badges`),
        axios.get(`${API}/profile`),
        axios.get(`${API}/subscription/status`)
      ]);
      setStreak(streakResp.data);
      setBadges(badgesResp.data);
      setProfile(profileResp.data);
      setSubscriptionStatus(subResp.data);
    } catch (err) {
      console.error(err);
    }
  };

  const handleGenerate = async () => {
    if (!topic.trim() && !selectedCategory) {
      toast.error('Please enter a topic or select a category');
      return;
    }

    // Check if user has reached limit
    if (subscriptionStatus && !subscriptionStatus.can_generate) {
      setShowUpgradePrompt(true);
      return;
    }

    setLoading(true);
    try {
      const resp = await axios.post(`${API}/devotionals/generate`, {
        topic: topic.trim() || selectedCategory,
        category: selectedCategory
      });
      navigate('/devotional', { state: { devotional: resp.data } });
      // Reload subscription status after generating
      const subResp = await axios.get(`${API}/subscription/status`);
      setSubscriptionStatus(subResp.data);
    } catch (err) {
      if (err.response?.status === 403) {
        setShowUpgradePrompt(true);
      } else {
        toast.error(err.response?.data?.detail || 'Failed to generate devotional');
      }
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = async (plan) => {
    try {
      const resp = await axios.post(`${API}/subscription/create-checkout`, { plan });
      window.location.href = resp.data.checkout_url;
    } catch (err) {
      toast.error('Failed to start checkout');
      console.error(err);
    }
  };

  const BADGE_INFO = {
    'first_reading': { name: 'First Reading', icon: '📖' },
    '5_day_streak': { name: '5-Day Streak', icon: '🔥' },
    '10_day_streak': { name: '10-Day Streak', icon: '⭐' },
    '30_day_streak': { name: '30-Day Streak', icon: '🏆' },
    '10_saved': { name: '10 Saved', icon: '❤️' },
    '50_readings': { name: '50 Readings', icon: '🌟' }
  };

  return (
    <div className="page-container pb-24">
      <div className="fade-in max-w-[1100px] mx-auto pb-20">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-4">
            <img src={LOGO_URL} alt="OmniRead" className="w-[60px] h-[60px]" />
            <div>
              <h1 className="text-3xl md:text-5xl font-bold tracking-tighter m-0 uppercase">OMNIREAD</h1>
              <p className="text-gold text-sm font-bold m-0 tracking-wide uppercase">Welcome, {user?.name}</p>
            </div>
          </div>

          <div className="flex justify-center flex-wrap items-center gap-4">
            <Button
              onClick={() => navigate('/collective-library')}
              data-testid="collective-library-nav"
              className="btn-outline-gold px-6 py-3 w-full md:w-auto"
            >
              <BookOpen size={20} className="mr-2" />
              Collective
            </Button>
            <Button
              onClick={() => navigate('/library')}
              data-testid="library-nav"
              className="btn-outline-gold px-6 py-3 w-full md:w-auto"
            >
              <Library size={20} className="mr-2" />
              My Library
            </Button>
          </div>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="mystical-card p-6 text-center">
            <Flame size={32} className="text-[#d4af37] mx-auto mb-2" />
            <h3 className="gold-text text-3xl md:text-4xl font-bold my-2 uppercase">{streak.current_streak}</h3>
            <p className="text-xs md:text-sm text-gray-500 m-0 uppercase tracking-widest font-bold">Day Streak</p>
          </div>

          <div className="mystical-card p-6 text-center">
            <Award size={32} className="text-[#d4af37] mx-auto mb-2" />
            <h3 className="gold-text text-3xl md:text-4xl font-bold my-2 uppercase">{badges.length}</h3>
            <p className="text-xs md:text-sm text-gray-500 m-0 uppercase tracking-widest font-bold">Badges Earned</p>
          </div>

          <div className="mystical-card p-6 text-center">
            <BookOpen size={32} className="text-[#d4af37] mx-auto mb-2" />
            <h3 className="gold-text text-3xl md:text-4xl font-bold my-2 uppercase">{profile?.selected_lenses?.length || 0}</h3>
            <p className="text-xs md:text-sm text-gray-500 m-0 uppercase tracking-widest font-bold">Active Lenses</p>
          </div>
        </div>

        {/* Main Input */}
        <div className="mystical-card mb-8 p-8">
          <h2 className="text-xl md:text-3xl font-bold mb-6 tracking-tight">What contemplation calls to you today?</h2>

          <Textarea
            data-testid="topic-input"
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            placeholder=" I'm struggling with patience...
            Tell me about rebirth...
            What does phoenix energy mean?
            I need guidance on relationships..."
            className="min-h-[150px] text-lg bg-black/40 border-gold/30 text-[#e8e8e8] rounded-md p-4 resize-y w-full"
          />

          <div className="mt-8">
            <h3 className="text-base font-bold mb-4 text-gold uppercase tracking-wider">Or choose a category:</h3>
            <div className="flex flex-wrap gap-3">
              {CATEGORIES.map(cat => (
                <button
                  key={cat}
                  data-testid={`category-${cat.toLowerCase().replace(/\//g, '-').replace(/\s+/g, '-')}`}
                  onClick={() => {
                    setSelectedCategory(cat);
                    setTopic('');
                  }}
                  className={`px-5 py-2.5 rounded-full text-sm font-bold cursor-pointer transition-all border ${selectedCategory === cat
                    ? 'bg-gold/20 border-gold text-gold ring-1 ring-gold/20'
                    : 'bg-black/30 border-gold/30 text-gray-400 hover:border-gold/50 hover:text-gray-300'
                    }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-8 text-center">
            <Button
              onClick={handleGenerate}
              data-testid="generate-devotional-button"
              className="btn-gold px-12 text-lg md:text-xl w-full md:w-auto"
              disabled={loading}
            >
              {loading ? 'Weaving wisdom...' : 'Generate Devotional'}
            </Button>
          </div>
        </div>

        {/* Usage Info Banner */}
        {subscriptionStatus && subscriptionStatus.tier === 'free' && (
          <div
            className="mystical-card mb-8 p-8"
            style={{
              background: 'linear-gradient(135deg, rgba(212, 175, 55, 0.1), rgba(212, 175, 55, 0.05))',
              border: '1px solid rgba(212, 175, 55, 0.3)'
            }}
          >
            <div className="flex flex-col md:flex-row items-center gap-6">
              <AlertCircle size={32} className="text-[#d4af37]" />
              <div className="flex-1 text-center md:text-left">
                <p className="m-0 text-base md:text-lg text-[#e8e8e8] font-bold">
                  {subscriptionStatus.readings_used}/{subscriptionStatus.readings_limit} readings used this month
                </p>
                <p className="mt-1 text-sm text-gray-500 font-medium">
                  Upgrade to Pro for unlimited devotionals and 6 wisdom lenses
                </p>
              </div>
              <Button
                onClick={() => setShowUpgradePrompt(true)}
                className="btn-gold w-full md:w-auto px-8"
              >
                <Crown size={18} className="mr-2" />
                Upgrade
              </Button>
            </div>
          </div>
        )}

        {/* Recent Badges */}
        {badges.length > 0 && (
          <div className="mystical-card p-8 mb-20">
            <h2 className="text-xl md:text-2xl font-bold mb-6 tracking-tight">Recent Achievements</h2>
            <div className="flex flex-wrap gap-4">
              {badges.slice(0, 6).map((badge, idx) => {
                const info = BADGE_INFO[badge.badge_type] || { name: badge.badge_type, icon: '🏅' };
                return (
                  <div key={idx} className="badge px-4 py-2">
                    <span className="text-2xl mr-2">{info.icon}</span>
                    <span className="font-bold text-sm">{info.name}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Upgrade Modal */}
      {showUpgradePrompt && (
        <div
          className="fixed inset-0 bg-black/85 flex items-center justify-center z-[9999] p-4 backdrop-blur-sm"
          onClick={() => setShowUpgradePrompt(false)}
        >
          <div
            className="mystical-card max-w-[500px] w-full bg-gradient-to-br from-[#14141e]/98 to-[#1e1e28]/98 border-2 border-gold p-8 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-center mb-8">
              <Crown size={64} className="text-[#d4af37] mx-auto mb-4" />
              <h2 className="text-2xl md:text-3xl font-bold mb-2">Upgrade to Pro</h2>
              <p className="text-gray-400 text-base m-0">
                You've reached your monthly limit of {subscriptionStatus?.readings_limit} readings
              </p>
            </div>

            <div className="mb-8 p-6 bg-gold/5 border border-gold/20 rounded-lg">
              <h3 className="text-base font-bold mb-4 text-gold uppercase tracking-wider">Pro Benefits:</h3>
              <ul className="list-none p-0 m-0 space-y-3 text-[#c9c9c9]">
                <li className="flex items-center gap-2">
                  <span className="text-gold">✨</span> Unlimited devotional readings
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-gold">🔮</span> Access to all 6 wisdom lenses
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-gold">⚡</span> Priority support
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-gold">🎯</span> Enhanced personalization
                </li>
              </ul>
            </div>

            <div className="flex flex-col gap-4">
              <Button
                onClick={() => handleUpgrade('yearly')}
                className="btn-gold w-full py-6 text-xl h-auto"
              >
                <Crown size={24} className="mr-2" />
                $39/year <span className="text-sm ml-2 opacity-80">(Save 35%)</span>
              </Button>
              <Button
                onClick={() => handleUpgrade('monthly')}
                className="btn-outline-gold w-full py-5 text-lg h-auto"
              >
                $4.99/month
              </Button>
              <button
                onClick={() => setShowUpgradePrompt(false)}
                className="mt-2 text-gray-500 hover:text-gold transition-colors text-sm font-bold uppercase tracking-widest"
              >
                Maybe Later
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}